// [TIMESERIES]: a row's own Date column is independently optional (real
// projects almost always omit it), so a row can be 3 tokens (Name, Time,
// Value) or 4 (Name, Date, Time, Value) independent of whether it's a
// series' first row.
//
// A `Name FILE pathname` row (external file as data source) is NOT
// editable here, but must round-trip verbatim (`fileRow` below): this used
// to misparse "FILE"/the path as date/time/value tokens, which
// `writeTimeSeries` then wrote back out as `NAME FILE NaN` — silently and
// PERMANENTLY destroying the external file reference the moment ANY other
// part of the project was saved, even without the user ever opening this
// series in the editor.

import { nextAvailableName } from "./nextAvailableName.ts";
import { parseNum } from "./parseNum.ts";

export interface TimeSeriesPoint {
  /** SWMM's own `MM/DD/YYYY`, or "" — most real series omit this and give elapsed time instead. */
  date: string;
  /** SWMM's own `H:MM` (or `H:MM:SS`), OR a plain decimal number of elapsed hours since the date/simulation start — both are valid SWMM syntax, kept as free text here rather than forced into one shape. */
  time: string;
  value: number;
}

export interface TimeSeries {
  name: string;
  points: TimeSeriesPoint[];
  /**
   * Verbatim tokens for a `Name FILE path` row — this editor can't edit a
   * FILE-sourced series' own path, so its raw row is kept as-is instead of
   * being force-parsed into garbage points (see this file's own header).
   * `writeTimeSeries` re-emits this verbatim as long as `points` stays
   * empty; adding an inline point makes `points` win instead.
   */
  fileRow?: string[];
}

export function readTimeSeries(
  raw: ReadonlyMap<string, string[][]>,
  warnings?: Set<string>
): TimeSeries[] {
  const series: TimeSeries[] = [];
  const byName = new Map<string, TimeSeries>();
  for (const row of raw.get("TIMESERIES") ?? []) {
    const name = row[0];
    if (!name) continue;
    let s = byName.get(name);
    if (!s) {
      s = { name, points: [] };
      byName.set(name, s);
      series.push(s);
    }
    if ((row[1] ?? "").toUpperCase() === "FILE") {
      s.fileRow = row;
      continue;
    }
    const hasDate = row.length >= 4;
    s.points.push({
      date: hasDate ? row[1] : "",
      time: hasDate ? row[2] : row[1],
      value: parseNum(hasDate ? row[3] : row[2], 0, `[TIMESERIES] "${name}" Value`, warnings),
    });
  }
  return series;
}

export function writeTimeSeries(
  raw: ReadonlyMap<string, string[][]>,
  series: TimeSeries[]
): Map<string, string[][]> {
  const rows: string[][] = [];
  for (const s of series) {
    if (s.fileRow && s.points.length === 0) {
      rows.push(s.fileRow);
      continue;
    }
    for (const p of s.points) {
      rows.push(
        p.date.trim()
          ? [s.name, p.date, p.time, String(p.value)]
          : [s.name, p.time, String(p.value)]
      );
    }
  }
  const next = new Map(raw);
  next.set("TIMESERIES", rows);
  return next;
}

export function nextAvailableTimeSeriesName(existingNames: Set<string>): string {
  return nextAvailableName(existingNames, "T");
}
